import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { garage } from "./garage";
import { type RouterOutputs, useTRPC } from "./trpc";

/**
 * The current vehicle (ADR 0008): the car being worked on. Lists show only what is relevant to
 * it. It lasts for this window (session storage, per garage), so every start of the app begins
 * with all vehicles.
 */

type Vehicle = RouterOutputs["vehicles"]["list"][number];

interface CurrentVehicle {
  /** Null for "all vehicles". */
  vehicle: Vehicle | null;
  /** What list queries pass as `vehicleId`; set right away, before the vehicle list loads. */
  vehicleId: string | null;
  setVehicleId: (id: string | null) => void;
}

const STORAGE_KEY = `bt-vehicle:${garage ?? ""}`;

function readStored(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Not kept; the choice still applies until the page reloads.
  }
}

const Context = createContext<CurrentVehicle>({
  vehicle: null,
  vehicleId: null,
  setVehicleId: () => {},
});

export function CurrentVehicleProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const { data: vehicles } = useQuery(trpc.vehicles.list.queryOptions());
  const [vehicleId, setStored] = useState(readStored);
  const vehicle = vehicles?.find((v) => v.id === vehicleId) ?? null;
  const setVehicleId = (id: string | null) => {
    writeStored(id);
    setStored(id);
  };
  // A vehicle that was archived or deleted can't stay current.
  useEffect(() => {
    if (vehicles && vehicleId && !vehicles.some((v) => v.id === vehicleId)) {
      writeStored(null);
      setStored(null);
    }
  }, [vehicles, vehicleId]);
  return (
    <Context.Provider value={{ vehicle, vehicleId, setVehicleId }}>{children}</Context.Provider>
  );
}

export function useCurrentVehicle(): CurrentVehicle {
  return useContext(Context);
}
